import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Grid, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { format } from 'date-fns';
import { Expense } from '../types/accounting';
import { accountingService } from '../services/accountingService';
import { projectService } from '../services/projectService';
import { userService } from '../services/userService';
import { Project } from '../types/project';
import { User } from '../types/user';
import AddExpenseModal from '../components/AddExpenseModal';

const ExpensePage: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // State for filters
  const [projectFilter, setProjectFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  // State for filter dropdown data
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Calculate summary totals
  const summary = useMemo(() => {
    const totalPaid = expenses
      .filter(exp => exp.status?.toLowerCase() === 'paid')
      .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
    
    const totalPayable = expenses
      .filter(exp => exp.status?.toLowerCase() === 'payable')
      .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
    
    const totalExpense = expenses
      .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
    
    return { totalPaid, totalPayable, totalExpense };
  }, [expenses]);

  const fetchExpenses = async (params: Parameters<typeof accountingService.getExpenses>[0]) => {
    try {
      setLoading(true);
      const data = await accountingService.getExpenses(params);
      setExpenses(data);
    } catch (err) {
      setError('Failed to fetch expenses.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [projectData, userData] = await Promise.all([
          projectService.getProjects(),
          userService.getUsers(),
        ]);
        setProjects(projectData);
        setUsers(userData);
      } catch (err) {
        console.error("Failed to fetch filter data", err);
      }
    };
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchExpenses({ project: projectFilter, submitted_by: userFilter });
  }, [projectFilter, userFilter]);

  const handleSave = () => {
    fetchExpenses({ project: projectFilter, submitted_by: userFilter });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Expenses</Typography>
        <Button variant="contained" onClick={() => setIsAddModalOpen(true)}>Add Expense</Button>
      </Box>

      {/* Summary Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Paid
              </Typography>
              <Typography variant="h5" color="success.main">
                ${summary.totalPaid.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Payable
              </Typography>
              <Typography variant="h5" color="warning.main">
                ${summary.totalPayable.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Expense
              </Typography>
              <Typography variant="h5" color="primary.main">
                ${summary.totalExpense.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Filter by Project</InputLabel>
            <Select value={projectFilter} label="Filter by Project" onChange={(e) => setProjectFilter(e.target.value)}>
              <MenuItem value=""><em>All Projects</em></MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Filter by User</InputLabel>
            <Select value={userFilter} label="Filter by User" onChange={(e) => setUserFilter(e.target.value)}>
              <MenuItem value=""><em>All Users</em></MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Expense Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Submitted By</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Remark</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{format(new Date(expense.date), 'MMM dd, yyyy')}</TableCell>
                <TableCell>{expense.project_details?.name || 'N/A'}</TableCell>
                <TableCell>{expense.expense_type}</TableCell>
                <TableCell>{expense.description}</TableCell>
                <TableCell>{expense.submitted_by_details?.first_name || 'N/A'}</TableCell>
                <TableCell>{expense.status}</TableCell>
                <TableCell>{expense.remark}</TableCell>
                <TableCell align="right">${parseFloat(expense.amount).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AddExpenseModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSave} />
    </Box>
  );
};

export default ExpensePage;